import React, { useEffect, useState } from 'react';
import {
  useSchedulingContext,
  useSchedulingUpdateContext,
} from '../../../context';
import DateTimeStepPresenter from './DateTimeStepPresenter';
import {
  DateTimeDetails,
  DateTimeFilter,
  LocationDetails,
  ProviderDetails,
  ProviderSummary,
  SlotDateGroup,
} from '../../../../api/models';
import { PresenterState } from './PresenterState';
import {
  GetUnauthenticatedAppointmentSlotsApiService,
  GetUnauthenticatedAppointmentSlotsRequest,
} from '../../../../api/services';
import { DateUtility } from '@mchs/web-core';
import { AllLocations, incrementDateMonths } from '../../../../utils';
import { swapProviders } from './DateTimeStepFunctions';

export interface LocationStepWrapperProps {
  onContinue: () => void;
}

const defaultRangeInMonths = 2;

const DateTimeStepWrapper: React.FC<LocationStepWrapperProps> = (
  props: LocationStepWrapperProps
) => {
  const schedulingDetails = useSchedulingContext();
  const schedulingUpdateContext = useSchedulingUpdateContext();

  const [presenterState, setPresenterState] = useState<PresenterState>(
    PresenterState.Ready
  );
  const [filter, setFilter] = useState<DateTimeFilter>(
    schedulingDetails.dateTimeDetails?.dateTimeFilter ?? {
      date: new Date(),
      thruDate: incrementDateMonths(new Date(), defaultRangeInMonths),
      locationId: schedulingDetails.locationDetails?.searchLocationId ?? '',
    }
  );
  const [slotResults, setSlotResults] = useState<SlotDateGroup[]>([]);
  const [alternateProviders, setAlternateProviders] = useState<
    ProviderSummary[]
  >([]);

  const [loaded, setLoaded] = useState<boolean>(false);

  useEffect(() => {
    setLoaded(true);
  }, []);

  useEffect(() => {
    if (loaded && schedulingDetails.dateTimeDetails?.slot) {
      props.onContinue();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schedulingDetails.dateTimeDetails?.slot]);

  useEffect(() => {
    const getAppointmentSlots = async () => {
      // clear any previous results
      setSlotResults([]);
      setAlternateProviders([]);

      if (
        schedulingDetails.providerDetails?.practitionerId &&
        filter.date &&
        filter.thruDate
      ) {
        setPresenterState(PresenterState.Loading);

        const request: GetUnauthenticatedAppointmentSlotsRequest = {
          //patientId: undefined,
          patientDob:
            schedulingDetails.patientDetails?.demographics?.dateOfBirth ??
            undefined,
          patientSex:
            schedulingDetails.patientDetails?.demographics?.preferredSex,
          providerId: schedulingDetails.providerDetails?.practitionerId,
          appointmentTypeId:
            schedulingDetails.appointmentInfo?.appointmentTypeId,
          questions: schedulingDetails.appointmentInfo?.questionAnswers?.map(
            (answer) => ({
              questionId: answer.questionId,
              answerId: answer.questionAnswerId,
            })
          ),
          locationId:
            filter.locationId === AllLocations ? '' : filter.locationId,
          searchStartDate: DateUtility.formatDateISOWithTimeZone(filter.date),
          searchEndDate: DateUtility.formatDateISOWithTimeZone(filter.thruDate),
        };

        const response =
          await GetUnauthenticatedAppointmentSlotsApiService.execute(request);
        if (response.succeeded && response.data) {
          setSlotResults(response.data.availableAppointmentSlots ?? []);
          setAlternateProviders(response.data.alternateProviders ?? []);
          setPresenterState(PresenterState.Ready);
        } else {
          setPresenterState(PresenterState.Error);
        }
      } else {
        setPresenterState(PresenterState.Error);
      }
    };

    getAppointmentSlots();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  const onFilterChanged = (
    date: Date | null | undefined,
    locationId: string
  ) => {
    const newFilter = {
      date: date,
      thruDate: date
        ? incrementDateMonths(date, defaultRangeInMonths)
        : undefined,
      locationId: locationId,
    };
    setFilter(newFilter);
    schedulingUpdateContext.changeSchedulingDetails({
      ...schedulingDetails,
      dateTimeDetails: {
        ...schedulingDetails.dateTimeDetails,
        dateTimeFilter: { ...newFilter },
        slot: undefined,
      },
    });
  };

  const onCareProviderSelected = async (
    newProviderDetails: ProviderDetails,
    availableAppointmentSlots?: SlotDateGroup[]
  ) => {
    setPresenterState(PresenterState.SwappingProviders);

    await swapProviders(
      filter.date ?? new Date(),
      schedulingDetails,
      schedulingUpdateContext,
      newProviderDetails,
      availableAppointmentSlots ?? [],
      slotResults,
      alternateProviders,
      setSlotResults,
      setAlternateProviders
    );

    setPresenterState(PresenterState.Ready);
  };

  const onContinue = (
    dateTimeDetails: DateTimeDetails,
    locationDetails: LocationDetails
  ) => {
    schedulingUpdateContext.changeSchedulingDetails({
      ...schedulingDetails,
      locationDetails: { ...locationDetails },
      dateTimeDetails: { ...dateTimeDetails, dateTimeFilter: { ...filter } },
    });
  };

  return (
    <DateTimeStepPresenter
      filter={filter}
      schedulingDetails={schedulingDetails}
      searchResults={slotResults}
      alternateProviders={alternateProviders}
      state={presenterState}
      onFilterChanged={onFilterChanged}
      onCareProviderSelected={onCareProviderSelected}
      onContinue={onContinue}
    />
  );
};

export default DateTimeStepWrapper;
